import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { MapPin, Mail, Linkedin, Twitter } from 'lucide-react';
export function WhoWeAre() {
  const team = [{
    name: 'Alex Rivera',
    role: 'Co-Founder & Education Lead',
    pronouns: 'they/them',
    location: 'Oakland, CA',
    image: 'bg-brand-lime',
    bio: 'Alex is a fat queer educator and writer with 10 years of experience in social justice education. They hold an MA in Education and have facilitated workshops on fat liberation, disability justice, and anti-racism for universities, nonprofits, and community organizations.',
    why: 'I started ReGrowth because I was tired of seeing fat liberation treated as a side issue or an afterthought. This work deserves the same rigor, resources, and respect as any other justice movement.',
    contact: {
      email: 'alex@regrowth.com',
      twitter: '@alexrivera'
    }
  }, {
    name: 'Jordan Smith',
    role: 'Co-Founder & Editor',
    pronouns: 'she/her',
    location: 'Chicago, IL',
    image: 'bg-brand-cyan',
    bio: 'Jordan is a fat disabled writer and editor whose work focuses on the intersection of disability justice and fat liberation. She has edited for literary magazines and worked as a community organizer for disability rights.',
    why: "As a fat disabled person, I've experienced how fatphobia and ableism compound. ReGrowth is my attempt to create the resource I wish I'd had—one that takes both identities seriously.",
    contact: {
      email: 'jordan@regrowth.com',
      linkedin: 'jordan-smith'
    }
  }, {
    name: 'Sam Chen',
    role: 'Editorial Director',
    pronouns: 'they/them',
    location: 'Brooklyn, NY',
    image: 'bg-brand-orange',
    bio: 'Sam is a fat Asian American writer and cultural critic. Their essays have appeared in The New Inquiry, Catapult, and elsewhere. They bring editorial experience from literary magazines and a deep commitment to centering marginalized voices.',
    why: "I believe in the power of storytelling to shift culture. The Digital Publication is my way of making space for fat people's stories—the messy, complicated, brilliant ones that don't fit neat narratives.",
    contact: {
      email: 'sam@regrowth.com',
      twitter: '@samchen'
    }
  }, {
    name: 'Riley Johnson',
    role: 'Operations & Community',
    pronouns: 'he/him',
    location: 'Portland, OR',
    image: 'bg-brand-magenta',
    bio: "Riley is a fat trans man who has worked in nonprofit operations and community organizing for 8 years. He manages ReGrowth's day-to-day operations, community programs, and ensures our spaces are accessible and welcoming.",
    why: "I'm here because I believe in building sustainable movements. That means taking care of our people, our resources, and our relationships. It's not glamorous work, but it's essential.",
    contact: {
      email: 'riley@regrowth.com'
    }
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1670">
      <HeroSection title="Who We Are" subtitle="The people behind ReGrowth." tag="About ReGrowth" bgColor="lime" textColor="navy" data-id="element-1671" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1672">
        <div className="prose prose-lg" data-id="element-1673">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1674">
            ReGrowth was founded in 2022 by two fat activists who were
            frustrated by the lack of{' '}
            <strong className="text-brand-lime" data-id="element-1675">
              rigorous, accessible, justice-led
            </strong>{' '}
            education around fat liberation. We're a small team of fat people
            committed to building something sustainable, accountable, and
            transformative.
          </p>
        </div>
      </section>

      {/* Team Profiles */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1676">
        <div className="space-y-16" data-id="element-1677">
          {team.map((person, index) => <div key={index} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 ${index % 2 === 0 ? '' : 'lg:grid-flow-dense'}`} data-id="element-1678">
              {/* Image Column */}
              <div className={`${index % 2 === 0 ? '' : 'lg:col-start-3'}`} data-id="element-1679">
                <div className={`aspect-square ${person.image} border-2 border-brand-black rounded-sm shadow-xl`} data-id="element-1680"></div>
                <div className="mt-4 space-y-2" data-id="element-1681">
                  <div className="flex items-center text-sm text-gray-600" data-id="element-1682">
                    <MapPin className="h-4 w-4 mr-2 text-brand-lime" data-id="element-1683" />
                    {person.location}
                  </div>
                  <div className="flex flex-wrap gap-3" data-id="element-1684">
                    {person.contact.email && <a href={`mailto:${person.contact.email}`} className="text-sm text-brand-lime hover:underline flex items-center" data-id="element-1685">
                        <Mail className="h-4 w-4 mr-1" data-id="element-1686" />
                        Email
                      </a>}
                    {person.contact.twitter && <a href="#" className="text-sm text-brand-lime hover:underline flex items-center" data-id="element-1687">
                        <Twitter className="h-4 w-4 mr-1" data-id="element-1688" />
                        Twitter
                      </a>}
                    {person.contact.linkedin && <a href="#" className="text-sm text-brand-lime hover:underline flex items-center" data-id="element-1689">
                        <Linkedin className="h-4 w-4 mr-1" data-id="element-1690" />
                        LinkedIn
                      </a>}
                  </div>
                </div>
              </div>

              {/* Content Column */}
              <div className={`lg:col-span-2 ${index % 2 === 0 ? '' : 'lg:col-start-1 lg:row-start-1'}`} data-id="element-1691">
                <div className="mb-4" data-id="element-1692">
                  <h3 className="text-3xl font-black uppercase text-brand-black mb-1" data-id="element-1693">
                    {person.name}
                  </h3>
                  <p className="text-brand-lime font-bold uppercase text-sm mb-1" data-id="element-1694">
                    {person.role}
                  </p>
                  <p className="text-gray-500 text-sm" data-id="element-1695">{person.pronouns}</p>
                </div>

                <div className="space-y-4" data-id="element-1696">
                  <div className="bg-white border-l-4 border-brand-lime p-6" data-id="element-1697">
                    <h4 className="font-bold text-sm uppercase text-gray-500 mb-2" data-id="element-1698">
                      Background
                    </h4>
                    <p className="text-gray-700 leading-relaxed" data-id="element-1699">
                      {person.bio}
                    </p>
                  </div>

                  <div className="bg-brand-lime/10 border-l-4 border-brand-lime p-6" data-id="element-1700">
                    <h4 className="font-bold text-sm uppercase text-gray-500 mb-2" data-id="element-1701">
                      Why ReGrowth?
                    </h4>
                    <p className="text-gray-700 leading-relaxed italic" data-id="element-1702">
                      "{person.why}"
                    </p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white" data-id="element-1703">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1704">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black text-center" data-id="element-1705">
            What Guides Us
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1706">
            <div className="p-6 border-2 border-brand-black" data-id="element-1707">
              <h3 className="text-xl font-bold text-brand-lime mb-3" data-id="element-1708">
                Lived Experience
              </h3>
              <p className="text-gray-700 text-sm" data-id="element-1709">
                We're all fat. We're not academics studying fatness from the
                outside—we live it. Our work is grounded in our own experiences
                and those of our community.
              </p>
            </div>

            <div className="p-6 border-2 border-brand-black" data-id="element-1710">
              <h3 className="text-xl font-bold text-brand-lime mb-3" data-id="element-1711">
                Intersectionality
              </h3>
              <p className="text-gray-700 text-sm" data-id="element-1712">
                We hold multiple identities and understand how systems of
                oppression intersect. Fat liberation cannot be separated from
                racial justice, disability justice, and trans liberation.
              </p>
            </div>

            <div className="p-6 border-2 border-brand-black" data-id="element-1713">
              <h3 className="text-xl font-bold text-brand-lime mb-3" data-id="element-1714">
                Accountability
              </h3>
              <p className="text-gray-700 text-sm" data-id="element-1715">
                We don't always get it right. When we cause harm, we commit to
                listening, acknowledging, and making material changes. We're
                accountable to our community.
              </p>
            </div>

            <div className="p-6 border-2 border-brand-black" data-id="element-1716">
              <h3 className="text-xl font-bold text-brand-lime mb-3" data-id="element-1717">
                Sustainability
              </h3>
              <p className="text-gray-700 text-sm" data-id="element-1718">
                We're in this for the long haul. We prioritize rest, boundaries,
                and care for ourselves and our team. Burnout doesn't serve the
                movement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us */}
      <section className="py-20 bg-brand-lime/10" data-id="element-1719">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1720">
          <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-1721">
            Want to Work With Us?
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-1722">
            We occasionally hire for contract positions and are always looking
            for guest facilitators, contributors, and collaborators.
          </p>
          <a href="mailto:team@regrowth.com" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-brand-navy bg-brand-lime hover:bg-brand-lime/90 transition-all shadow-lg hover:-translate-y-1 uppercase tracking-wide" data-id="element-1723">
            Get in Touch
          </a>
        </div>
      </section>
    </div>;
}